/** Growable little-endian byte writer for snapshot encoding. */
export class ByteWriter {
  #buf: Uint8Array;
  #view: DataView;
  #pos = 0;

  constructor(initialSize = 1024) {
    this.#buf = new Uint8Array(initialSize);
    this.#view = new DataView(this.#buf.buffer);
  }

  get position(): number {
    return this.#pos;
  }

  #ensure(n: number): void {
    if (this.#pos + n <= this.#buf.length) return;
    let size = this.#buf.length * 2;
    while (size < this.#pos + n) size *= 2;
    const next = new Uint8Array(size);
    next.set(this.#buf.subarray(0, this.#pos));
    this.#buf = next;
    this.#view = new DataView(next.buffer);
  }

  u8(v: number): void {
    this.#ensure(1);
    this.#view.setUint8(this.#pos, v);
    this.#pos += 1;
  }
  u16(v: number): void {
    this.#ensure(2);
    this.#view.setUint16(this.#pos, v, true);
    this.#pos += 2;
  }
  u32(v: number): void {
    this.#ensure(4);
    this.#view.setUint32(this.#pos, v, true);
    this.#pos += 4;
  }
  i16(v: number): void {
    this.#ensure(2);
    this.#view.setInt16(this.#pos, v, true);
    this.#pos += 2;
  }
  i32(v: number): void {
    this.#ensure(4);
    this.#view.setInt32(this.#pos, v, true);
    this.#pos += 4;
  }
  f32(v: number): void {
    this.#ensure(4);
    this.#view.setFloat32(this.#pos, v, true);
    this.#pos += 4;
  }
  f64(v: number): void {
    this.#ensure(8);
    this.#view.setFloat64(this.#pos, v, true);
    this.#pos += 8;
  }
  /** Unsigned LEB128 (1 byte below 128). For counts, lengths, ids. */
  varuint(v: number): void {
    if (!Number.isInteger(v) || v < 0 || v > 0xffffffff)
      throw new RangeError(`varuint out of range: ${v}`);
    let x = v >>> 0;
    do {
      let byte = x & 0x7f;
      x >>>= 7;
      if (x !== 0) byte |= 0x80;
      this.u8(byte);
    } while (x !== 0);
  }

  /** Append raw bytes. */
  bytes(b: Uint8Array): void {
    this.#ensure(b.length);
    this.#buf.set(b, this.#pos);
    this.#pos += b.length;
  }

  /**
   * Start a length-prefixed section (a struct body), written in place without a temporary buffer.
   * One byte is reserved for the length; endSection() shifts bodies of 128+ bytes to make room.
   */
  beginSection(): number {
    this.u8(0);
    return this.#pos;
  }

  /** Write the section's byte length (varuint) in front of it. `start` is from beginSection(). */
  endSection(start: number): void {
    const length = this.#pos - start;
    let extra = 0;
    for (let x = length >>> 7; x !== 0; x >>>= 7) extra++;
    if (extra > 0) {
      this.#ensure(extra);
      this.#buf.copyWithin(start + extra, start, this.#pos);
      this.#pos += extra;
    }
    let at = start - 1;
    let x = length;
    while (x >= 0x80) {
      this.#buf[at++] = (x & 0x7f) | 0x80;
      x >>>= 7;
    }
    this.#buf[at] = x;
  }

  /** The written bytes (a copy, so the writer can be reused). */
  finish(): Uint8Array {
    return this.#buf.slice(0, this.#pos);
  }
}

/** Bounds-checked little-endian reader. Throws on truncated input instead of returning garbage. */
export class ByteReader {
  readonly #bytes: Uint8Array;
  readonly #view: DataView;
  #pos: number;
  #end: number;

  constructor(bytes: Uint8Array, start = 0, end = bytes.length) {
    this.#bytes = bytes;
    this.#view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    this.#pos = start;
    this.#end = end;
  }

  get position(): number {
    return this.#pos;
  }
  get end(): number {
    return this.#end;
  }

  #need(n: number): number {
    if (this.#pos + n > this.#end)
      throw new RangeError(
        `truncated snapshot: need ${n} byte(s) at ${this.#pos}, end ${this.#end}`,
      );
    const at = this.#pos;
    this.#pos += n;
    return at;
  }

  u8(): number {
    return this.#view.getUint8(this.#need(1));
  }
  u16(): number {
    return this.#view.getUint16(this.#need(2), true);
  }
  u32(): number {
    return this.#view.getUint32(this.#need(4), true);
  }
  i16(): number {
    return this.#view.getInt16(this.#need(2), true);
  }
  i32(): number {
    return this.#view.getInt32(this.#need(4), true);
  }
  f32(): number {
    return this.#view.getFloat32(this.#need(4), true);
  }
  f64(): number {
    return this.#view.getFloat64(this.#need(8), true);
  }
  varuint(): number {
    let result = 0;
    let shift = 0;
    for (;;) {
      const byte = this.u8();
      result += (byte & 0x7f) * 2 ** shift;
      if ((byte & 0x80) === 0) break;
      shift += 7;
      if (shift > 28) throw new RangeError("varuint too long");
    }
    if (result > 0xffffffff) throw new RangeError(`varuint out of range: ${result}`);
    return result;
  }

  /** The next `length` bytes, as a view (no copy). */
  bytes(length: number): Uint8Array {
    const at = this.#need(length);
    return this.#bytes.subarray(at, at + length);
  }

  /**
   * Restrict reads to the next `length` bytes (a struct body), so a struct can never read past its own
   * end. Returns the outer bound to pass to endSection().
   */
  beginSection(length: number): number {
    if (this.#pos + length > this.#end)
      throw new RangeError(
        `truncated snapshot: section of ${length} byte(s) at ${this.#pos}, end ${this.#end}`,
      );
    const outer = this.#end;
    this.#end = this.#pos + length;
    return outer;
  }

  /** Skip the rest of the section (fields appended by newer versions) and restore the outer bound. */
  endSection(outer: number): void {
    this.#pos = this.#end;
    this.#end = outer;
  }
}
